import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Typography,
  Button,
  Spin,
  Row,
  Col,
  Space,
  Badge,
  Tag,
  Card,
  Checkbox,
} from "antd";
import {
  CarOutlined,
  UserOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  PhoneOutlined,
  CommentOutlined,
  CarFilled,
  InfoOutlined,
} from "@ant-design/icons";
import api from "../../../../axiosConfig";
import dayjs from "../../../../utils/dayjs";
import { formatCurrencyCOP } from "../../../../utils/formatCurrency";
import ObservacionesDialog from "../../Dialog/ObservacionesDialog";
// Mismo menú contextual de Centro de Control, con los permisos de Ver traslados del módulo Autorizaciones
import MenuContextualTraslado from "../../CenterControl/MenuContextualTraslado";
import useNativeWebSocket from "../../../../hooks/useNativeWebSocket";
import { useUser } from "../../../../UserContext";

const { Title, Text } = Typography;

const TransfersModal = ({ autorizacionId, onClose }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [observacionesOpen, setObservacionesOpen] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  // Actualizar origen - destino: selección (casilla por traslado y "seleccionar todos") y diálogo
  const { user: currentUser } = useUser();
  const puedeActualizarOrigenDestino = Boolean(
    currentUser?.modulos?.some((m) => m.key?.toLowerCase() === "autorizaciones" && m.estado === "disponible") &&
      currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo?.toLowerCase() === "autorizaciones" && mp.permiso === "ACTUALIZAR_ORIGEN_DESTINO_A"
      )
  );
  const [seleccionados, setSeleccionados] = useState([]);
  const toggleSeleccion = (id) =>
    setSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const fetchTransfers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/traslados/por-autorizacion/${autorizacionId}`
      );

      if (Array.isArray(response.data)) {
        setTransfers(response.data);
      } else {
        setError("Formato de datos inesperado");
      }
    } catch (error) {
      setError("Error al cargar los traslados");
    } finally {
      setLoading(false);
    }
  }, [autorizacionId]);

  // Hook para WebSocket - actualización en tiempo real de traslados
  // Crear un setter personalizado que filtre por autorizacion_id
  const setFilteredTransfers = useCallback((newTransfers) => {
    if (typeof newTransfers === 'function') {
      setTransfers(prevTransfers => {
        const updated = newTransfers(prevTransfers);
        // Filtrar solo traslados de esta autorización
        return Array.isArray(updated) 
          ? updated.filter(t => t.autorizacion_id === parseInt(autorizacionId))
          : updated;
      });
    } else {
      // Si es un array directo, filtrarlo
      const filtered = Array.isArray(newTransfers) 
        ? newTransfers.filter(t => t.autorizacion_id === parseInt(autorizacionId))
        : newTransfers;
      setTransfers(filtered);
    }
  }, [autorizacionId]);

  // Usar WebSocket nativo para actualizaciones en tiempo real
  useNativeWebSocket(transfers, setFilteredTransfers, null, null, {});

  useEffect(() => {
    if (autorizacionId) {
      fetchTransfers();
    }
  }, [autorizacionId, fetchTransfers]);

  // Función para manejar la apertura del modal de observaciones
  const handleOpenObservaciones = (e, transferId) => {
    e.stopPropagation();
    setSelectedTransferId(transferId);
    setObservacionesOpen(true);
  };

  // Función para manejar el cierre del modal de observaciones
  const handleCloseObservaciones = () => {
    setObservacionesOpen(false);
  };

  // Función para manejar el menú contextual
  const handleContextMenu = (e, transfer) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedTransfer(transfer);
    setContextMenu({
      mouseX: e.clientX,
      mouseY: e.clientY,
    });
  };

  // Función para cerrar el menú contextual (el traslado se conserva: los diálogos del menú siguen abiertos)
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Copiar teléfonos desde el submenú del menú contextual (igual que Centro de Control)
  const handleOptionClick = (option) => {
    if (!selectedTransfer) return;
    let telefono = null;
    if (option === "copyPhone") {
      telefono = selectedTransfer.numero_contacto_paciente;
    } else if (option === "copyResponsiblePhone") {
      telefono = selectedTransfer.numero_contacto_responsable;
    } else if (option.startsWith("copyResponsiblePhone:")) {
      telefono = option.split(":")[1];
    }
    if (telefono) navigator.clipboard.writeText(telefono);
  };

  // Función para manejar cambios de estado
  const handleEstadoCambiado = useCallback((trasladoId, nuevoValor, campo) => {
    setTransfers(prevTransfers => 
      prevTransfers.map(transfer => 
        transfer.id === parseInt(trasladoId) 
          ? { ...transfer, ...(typeof nuevoValor === 'object' ? nuevoValor : { [campo]: nuevoValor }) }
          : transfer
      )
    );
  }, []);

  // Función para obtener coordenadas de origen y destino
  const getOriginDestination = (transfer) => {
    // Intentar parsear coordenadas si están disponibles
    let origin = null;
    let destination = null;
    
    // Si hay coordenadas de origen
    if (transfer.origen_coordenadas) {
      try {
        // Si ya es un objeto, usarlo directamente
        if (typeof transfer.origen_coordenadas === 'object') {
          const coords = transfer.origen_coordenadas;
          // Verificar formato GeoJSON
          if (coords.type === 'Point' && coords.coordinates && coords.coordinates.length === 2) {
            // GeoJSON format: [longitude, latitude]
            origin = { lat: coords.coordinates[1], lng: coords.coordinates[0] };
          } else if (coords.lat && coords.lng) {
            // Formato lat/lng directo
            origin = { lat: coords.lat, lng: coords.lng };
          }
        } else if (typeof transfer.origen_coordenadas === 'string') {
          // Intentar parsear como JSON
          try {
            const coords = JSON.parse(transfer.origen_coordenadas);
            if (coords.type === 'Point' && coords.coordinates) {
              origin = { lat: coords.coordinates[1], lng: coords.coordinates[0] };
            } else if (coords.lat && coords.lng) {
              origin = { lat: coords.lat, lng: coords.lng };
            }
          } catch (e) {
            // Si falla el parse JSON, intentar con formato string "lat,lng"
            const match = transfer.origen_coordenadas.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
            if (match) {
              origin = { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }
          }
        }
      } catch (e) {
        // Error silencioso, origin permanece null
      }
    }
    
    // Si hay coordenadas de destino
    if (transfer.destino_coordenadas) {
      try {
        // Si ya es un objeto, usarlo directamente
        if (typeof transfer.destino_coordenadas === 'object') {
          const coords = transfer.destino_coordenadas;
          // Verificar formato GeoJSON
          if (coords.type === 'Point' && coords.coordinates && coords.coordinates.length === 2) {
            // GeoJSON format: [longitude, latitude]
            destination = { lat: coords.coordinates[1], lng: coords.coordinates[0] };
          } else if (coords.lat && coords.lng) {
            // Formato lat/lng directo
            destination = { lat: coords.lat, lng: coords.lng };
          }
        } else if (typeof transfer.destino_coordenadas === 'string') {
          // Intentar parsear como JSON
          try {
            const coords = JSON.parse(transfer.destino_coordenadas);
            if (coords.type === 'Point' && coords.coordinates) {
              destination = { lat: coords.coordinates[1], lng: coords.coordinates[0] };
            } else if (coords.lat && coords.lng) {
              destination = { lat: coords.lat, lng: coords.lng };
            }
          } catch (e) {
            // Si falla el parse JSON, intentar con formato string "lat,lng"
            const match = transfer.destino_coordenadas.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
            if (match) {
              destination = { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
            }
          }
        }
      } catch (e) {
        // Error silencioso, destination permanece null
      }
    }
    
    return { origin, destination };
  };

  // Funciones para obtener colores según el estado
  const getStatusColor = (status) => {
    const statusColors = {
      Cancelado: "#b10e1a",
      Finalizado: "#4d4d4d",
      Fallido: "#4a2c04",
      FallidoXvalidar: "#d4380d",
      FinalizadoXvalidar: "#722ED1",
      Asignado: "#1f7362",
      "Pre-Asignado": "#0c27a0",
      Programado: "#6c06b5",
      Enterado: "#0c5b07",
      Iniciado: "#fa7114",
      Aceptado: "#088A08",
      "En Camino": "#FFBF00",
      Esperando: "#a44706",
      default: "#b0b500",
    };
    return statusColors[status] || statusColors.default;
  };

  const getConfirmacionColor = (confirmacion) => {
    return confirmacion === "Confirmado" ? "#52c41a" : "#f5222d";
  };

  const getSeguimientoColor = (seguimiento) => {
    return seguimiento === "Validado" ? "#52c41a" : "#f5222d";
  };

  const getInicioStatusColor = (inicioStatus) => {
    const inicioStatusColors = {
      "Inicio Exitosamente": "#52c41a",
      "Ubicación No Válida para Iniciar": "#faad14",
      "Sin Conectividad Para Iniciar": "#faad14",
    };
    return inicioStatusColors[inicioStatus] || "#faad14";
  };

  const getFinStatusColor = (finStatus) => {
    const finStatusColors = {
      "Finalizó Exitosamente": "#52c41a",
      "Ubicación No Válida para Finalizar": "#faad14",
      "Sin Conectividad Para Finalizar": "#faad14",
    };
    return finStatusColors[finStatus] || "#faad14";
  };

  // Versión vigente del traslado del menú (cambia con las acciones del menú y el WebSocket)
  const trasladoMenu =
    selectedTransfer && (transfers.find((t) => t.id === selectedTransfer.id) || selectedTransfer);

  return (
    <Modal
      title={
        <div
          style={{
            background: "linear-gradient(90deg, #0a2e5c, #004d99)",
            padding: "10px",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <CarOutlined style={{ marginRight: "8px" }} />
          <span>Traslados de la Autorización {autorizacionId}</span>
        </div>
      }
      open={true}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      width="90%"
      maxWidth={1100}
      style={{ maxHeight: "80vh", overflow: "auto" }}
      centered
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "24px" }}>
          <Spin size="large" />
          <Text style={{ display: "block", marginTop: 10 }}>
            Cargando traslados...
          </Text>
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Title level={4} type="danger">
            Error
          </Title>
          <Text>{error}</Text>
          <div style={{ marginTop: 20 }}>
            <Button type="primary" onClick={fetchTransfers}>
              Reintentar
            </Button>
          </div>
        </div>
      ) : transfers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Title level={4}>
            No se encontraron traslados para esta autorización
          </Title>
          <Text type="secondary">ID de autorización: {autorizacionId}</Text>
          <div style={{ marginTop: 20 }}>
            <Button type="primary" onClick={fetchTransfers}>
              Reintentar
            </Button>
          </div>
        </div>
      ) : (
        <>
        {puedeActualizarOrigenDestino && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <Checkbox
              checked={transfers.length > 0 && transfers.every((t) => seleccionados.includes(t.id))}
              indeterminate={seleccionados.length > 0 && !transfers.every((t) => seleccionados.includes(t.id))}
              onChange={(e) => setSeleccionados(e.target.checked ? transfers.map((t) => t.id) : [])}
            >
              Seleccionar todos ({transfers.length})
            </Checkbox>
            {seleccionados.length > 0 && (
              <Text type="secondary">
                {seleccionados.length} seleccionado(s): clic derecho en uno de ellos para actualizar origen - destino
              </Text>
            )}
          </div>
        )}
        {transfers.map((transfer) => (
          <Card
              key={transfer.id}
              style={{
                marginBottom: "16px",
                borderLeft: `4px solid ${getStatusColor(transfer.status)}`,
                borderRadius: "8px",
                cursor: "context-menu",
              }}
              styles={{ body: { padding: "16px", margin: 0 } }}
              onContextMenu={(e) => handleContextMenu(e, transfer)}
            >
            {/* Encabezado con ID y estados */}
            <div
              style={{
                backgroundColor: "#0a2e5c",
                padding: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderRadius: "4px",
                marginBottom: "10px",
                flexWrap: "wrap",
              }}
            >
              <Space>
                {puedeActualizarOrigenDestino && (
                  <Checkbox
                    checked={seleccionados.includes(transfer.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSeleccion(transfer.id)}
                    aria-label={`Seleccionar traslado ${transfer.id}`}
                  />
                )}
                <Text
                  strong
                  style={{
                    color: "#fff",
                    marginRight: "8px",
                    wordBreak: "break-all",
                  }}
                >
                  CS-{transfer.id}
                </Text>
              </Space>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {/* Tag de Estado del Traslado */}
                <Tag color={getStatusColor(transfer.status)}>
                  {transfer.status}
                </Tag>

                {/* Tag de Confirmación del Traslado con Icono */}
                <Tag
                  color={getConfirmacionColor(transfer.confirmacion)}
                  icon={
                    transfer.confirmacion === "Confirmado" ? (
                      <CheckCircleOutlined />
                    ) : null
                  }
                >
                  {transfer.confirmacion === "Confirmado"
                    ? "Confirmado"
                    : "Sin Confirmar"}
                </Tag>

                {/* Tag de Inicio Status - Solo si existe */}
                {transfer.inicio_status && (
                  <Tag color={getInicioStatusColor(transfer.inicio_status)}>
                    {transfer.inicio_status}
                  </Tag>
                )}

                {/* Tag de Fin Status - Solo si existe */}
                {transfer.fin_status && (
                  <Tag color={getFinStatusColor(transfer.fin_status)}>
                    {transfer.fin_status}
                  </Tag>
                )}

                {/* Tag de Seguimiento */}
                <Tag
                  color={getSeguimientoColor(transfer.seguimiento)}
                  icon={
                    transfer.seguimiento === "Validado" ? (
                      <CheckCircleFilled />
                    ) : null
                  }
                >
                  {transfer.seguimiento === "Validado"
                    ? "Validado"
                    : "Sin Validar"}
                </Tag>
              </div>
            </div>

            {/* Distribución de 3 columnas */}
            <Row gutter={[16, 16]}>
              {/* Columna izquierda: Fechas y horas */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size="small">
                  <Space>
                    <ClockCircleOutlined
                      style={{ fontSize: "16px", color: "#1890ff" }}
                    />
                    <Text>{dayjs(transfer.fecha).format("DD/MM/YYYY")}</Text>
                  </Space>
                  <Space>
                    <ClockCircleOutlined
                      style={{ fontSize: "16px", color: "#1890ff" }}
                    />
                    <Text>
                      Hora Recogida:{" "}
                      {transfer.hora_recogida?.slice(0, 5) || "N/A"}
                    </Text>
                  </Space>
                  <Space>
                    <ClockCircleOutlined
                      style={{ fontSize: "16px", color: "#1890ff" }}
                    />
                    <Text>
                      Hora Llegada:{" "}
                      {transfer.hora_llegada?.slice(0, 5) || "N/A"}
                    </Text>
                  </Space>
                </Space>
              </Col>

              {/* Columna central: Origen y destino */}
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size="small">
                  <Space>
                    <EnvironmentOutlined
                      style={{
                        fontSize: "18px",
                        color: transfer.origen_lugar ? "#52c41a" : "#cccccc",
                        filter: transfer.origen_lugar
                          ? "drop-shadow(0 0 2px rgba(82, 196, 26, 0.4))"
                          : "none",
                      }}
                    />{" "}
                    <Text>
                      <span style={{ fontWeight: "bold" }}>
                        {transfer.origen_lugar || "N/A"}
                      </span>{" "}
                      {transfer.origen_direccion} - {transfer.origen_ciudad}
                    </Text>
                  </Space>
                  <Space>
                    <EnvironmentOutlined
                      style={{
                        fontSize: "18px",
                        color: transfer.destino_lugar ? "#f5222d" : "#cccccc",
                        filter: transfer.destino_lugar
                          ? "drop-shadow(0 0 2px rgba(245, 34, 45, 0.4))"
                          : "none",
                      }}
                    />{" "}
                    <Text>
                      <span style={{ fontWeight: "bold" }}>
                        {transfer.destino_lugar || "N/A"}
                      </span>{" "}
                      {transfer.destino_direccion} - {transfer.destino_ciudad}
                    </Text>
                  </Space>
                </Space>
              </Col>

              {/* Columna derecha: Información del paciente */}
              <Col xs={24} sm={24} md={8} style={{ textAlign: "right" }}>
                <Space direction="vertical" size="small">
                  <Space>
                    <UserOutlined
                      style={{ fontSize: "16px", color: "#1890ff" }}
                    />
                    <Text>{transfer.nombre_paciente}</Text>
                    {transfer.observaciones_count > 0 && (
                      <Badge
                        count={transfer.observaciones_count}
                        style={{
                          backgroundColor: "#52c41a",
                          boxShadow: "0 0 0 2px rgba(82, 196, 26, 0.2)",
                          cursor: "pointer",
                        }}
                        title={`${transfer.observaciones_count} observaciones`}
                      >
                        <CommentOutlined
                          style={{
                            fontSize: "18px",
                            color: "#1890ff",
                            filter:
                              "drop-shadow(0 0 2px rgba(24, 144, 255, 0.2))",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                          }}
                          onClick={(e) =>
                            handleOpenObservaciones(e, transfer.id)
                          }
                        />
                      </Badge>
                    )}
                  </Space>
                  <Space>
                    <Text style={{ color: "#8c8c8c" }}>
                      {transfer.tipo_documento_paciente}
                    </Text>
                  </Space>
                  <Space>
                    <IdcardOutlined
                      style={{ fontSize: "16px", color: "#1890ff" }}
                    />
                    <Text>{transfer.numero_documento_paciente}</Text>
                  </Space>
                  {transfer.numero_contacto_paciente && (
                    <Space>
                      <PhoneOutlined
                        style={{ fontSize: "16px", color: "#1890ff" }}
                      />
                      <Text>{transfer.numero_contacto_paciente}</Text>
                    </Space>
                  )}
                </Space>
              </Col>
            </Row>

            {/* Empresa y Copago */}
            <Row
              gutter={[16, 16]}
              style={{ marginTop: "10px", textAlign: "center" }}
            >
              <Col
                xs={24}
                sm={12}
                md={12}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <MedicineBoxOutlined
                  style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
                />
                <Text>
                  {transfer.empresa}
                  {transfer.regimen_id && (
                    <>
                      {" "}
                      <span style={{ fontWeight: "bold" }}> - </span>
                      {transfer.regimen_nombre || ""}
                    </>
                  )}
                </Text>
              </Col>
              <Col
                xs={24}
                sm={12}
                md={12}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <DollarOutlined
                  style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
                />
                <Text>
                  {transfer.copago_diario
                    ? formatCurrencyCOP(transfer.copago_diario)
                    : "$ 0,00"}
                </Text>
              </Col>
            </Row>

            {/* Autorizaciones */}
            {(transfer.autorizacion || transfer.preautorizacion) && (
              <Row
                gutter={[16, 16]}
                style={{ marginTop: "10px", textAlign: "center" }}
              >
                {transfer.autorizacion && (
                  <Col xs={24} sm={12} md={12}>
                    <Space>
                      <InfoCircleOutlined
                        style={{ fontSize: "16px", color: "#1890ff" }}
                      />
                      <Text>
                        <strong>Número de Autorización:</strong>{" "}
                        {transfer.autorizacion}
                      </Text>
                    </Space>
                  </Col>
                )}
                {transfer.preautorizacion && (
                  <Col xs={24} sm={12} md={12}>
                    <Space>
                      <InfoCircleOutlined
                        style={{ fontSize: "16px", color: "#1890ff" }}
                      />
                      <Text>
                        <strong>Número de Preautorización:</strong>{" "}
                        {transfer.preautorizacion}
                      </Text>
                    </Space>
                  </Col>
                )}
              </Row>
            )}

            {/* Trayecto, duración, distancia y observaciones */}
            <Row
              gutter={[16, 16]}
              style={{ padding: "0 10px", textAlign: "center" }}
            >
              <Col xs={24} sm={12} md={8}>
                <Space>
                  <CarOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                  <Text> {transfer.trayecto}</Text>
                </Space>
              </Col>
              {/*transfer.duracion_estimada &&
                Number(transfer.duracion_estimada) > 0 && (
                  <Col xs={24} sm={12} md={8}>
                    <Space>
                      <ClockCircleOutlined />
                      <Text>{transfer.duracion_estimada} min</Text>
                    </Space>
                  </Col>
                )}
              {transfer.distancia_estimada && (
                <Col xs={24} sm={12} md={8}>
                  <Space>
                    <CarFilled />
                    <Text>{transfer.distancia_estimada} km</Text>
                  </Space>
                </Col>
              )*/}
              {transfer.observaciones_traslado && (
                <Col xs={24} sm={24} md={24}>
                  <Space>
                    <InfoCircleOutlined
                      style={{ fontSize: "16px", color: "#faad14" }}
                    />
                    <Text style={{ color: "orange" }}>
                      {transfer.observaciones_traslado}
                    </Text>
                  </Space>
                </Col>
              )}
              {transfer.indicaciones_conductor && (
                <Col xs={24} sm={24} md={24}>
                  <Space>
                    <InfoOutlined
                      style={{ fontSize: "16px", color: "#faad14" }}
                    />
                    <Text style={{ color: "orange" }}>
                      {transfer.indicaciones_conductor}
                    </Text>
                  </Space>
                </Col>
              )}
            </Row>

            {/* Tipo de traslado, vehículo y placa */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                marginTop: "10px",
              }}
            >
              {/* Tipo de viaje en el lado izquierdo */}
              <Text
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "5px",
                }}
              >
                {transfer.tipo_traslado} - {transfer.tipo_viaje}
              </Text>

              {/* Si hay vehículo asignado, mostrar en el lado derecho */}
              {transfer.nombre_vehiculo && transfer.placa && (
                <Text style={{ display: "flex", alignItems: "center" }}>
                  <CarFilled
                    style={{
                      fontSize: "16px",
                      color: "#1890ff",
                      marginRight: 8,
                    }}
                  />{" "}
                  {transfer.nombre_vehiculo} - {transfer.placa}
                </Text>
              )}
            </div>
          </Card>
        ))}
        </>
      )}

      {/* Modal de Observaciones */}
      {selectedTransferId && (
        <ObservacionesDialog
          open={observacionesOpen}
          onClose={handleCloseObservaciones}
          trasladoId={selectedTransferId}
          title={`Observaciones - CS-${selectedTransferId}`}
          key={`observaciones-${selectedTransferId}`}
        />
      )}

      {/* Menú Contextual (el traslado se toma de la lista para reflejar los cambios hechos desde el menú) */}
      {selectedTransfer && (
        <MenuContextualTraslado
          modulo="Autorizaciones"
          contextMenu={contextMenu}
          handleCloseContextMenu={handleCloseContextMenu}
          handleOptionClick={handleOptionClick}
          trasladoId={selectedTransfer.id}
          onEstadoCambiado={handleEstadoCambiado}
          origin={getOriginDestination(trasladoMenu).origin}
          destination={getOriginDestination(trasladoMenu).destination}
          traslado={trasladoMenu}
          seleccionados={seleccionados}
          onOrigenDestinoActualizado={() => {
            setSeleccionados([]);
            fetchTransfers();
          }}
        />
      )}
    </Modal>
  );
};

export default TransfersModal;
